import React, { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AddAPhoto,
  CameraAlt,
  CheckCircle,
  Close,
  CloudUpload,
  Gavel,
  NewReleases,
  PendingActions,
} from '@mui/icons-material';
import {
  fetchPhotoQueue,
  patchPhotoRequirementsWaived,
  uploadPhotoQueueImage,
} from '../api/photoQueue';
import type {
  PhotoAngle,
  PhotoQueueExtraPhoto,
  PhotoQueueFilter,
  PhotoQueueItem,
  PhotoQueueSummary,
  PhotoUploadAngle,
} from '../types/photoQueue';
import {
  NEW_ITEM_ANGLES,
  PHOTO_ANGLE_LABELS,
  PHOTO_EXTRA_ANGLE_LABEL,
  USED_ITEM_ANGLES,
} from '../types/photoQueue';

/**
 * Talla con sufijo "cm" solo si el valor es un número. Si no, se muestra el texto tal cual (p. ej. talla de letra).
 */
function tallaConUnidad(sizeCm: string | null | undefined): string {
  if (sizeCm == null) return '—';
  const s = String(sizeCm).trim();
  if (s === '') return '—';
  const n = Number(s);
  if (Number.isFinite(n) && !Number.isNaN(n)) {
    return `${s} cm`;
  }
  return s;
}

// ─── Photo slot card inside the upload modal ──────────────────────────────────

interface PhotoSlotProps {
  angle: PhotoAngle;
  url: string | null;
  isUploading: boolean;
  error: string | null;
  onSelect: (angle: PhotoAngle) => void;
}

function PhotoSlotCard({ angle, url, isUploading, error, onSelect }: PhotoSlotProps) {
  const label = PHOTO_ANGLE_LABELS[angle];

  const handleBoxClick = useCallback(() => {
    if (!isUploading) onSelect(angle);
  }, [isUploading, onSelect, angle]);

  return (
    <Box>
      <Typography
        variant="caption"
        sx={{
          display: 'block',
          mb: 0.75,
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: 0.5,
          color: 'text.secondary',
        }}
      >
        {label}
        {url && (
          <CheckCircle
            sx={{ fontSize: 12, ml: 0.5, color: 'success.main', verticalAlign: 'middle' }}
          />
        )}
      </Typography>
      <Box
        onClick={handleBoxClick}
        sx={{
          height: 160,
          borderRadius: 2,
          overflow: 'hidden',
          border: url ? 'none' : '2px dashed',
          borderColor: error ? 'error.main' : 'divider',
          bgcolor: url ? 'transparent' : 'grey.50',
          cursor: isUploading ? 'wait' : 'pointer',
          position: 'relative',
          transition: 'border-color 0.15s',
          '&:hover': {
            borderColor: !url && !isUploading ? 'text.primary' : undefined,
          },
        }}
      >
        {isUploading && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'rgba(255,255,255,0.75)',
              zIndex: 2,
            }}
          >
            <CircularProgress size={32} sx={{ color: '#111827' }} />
          </Box>
        )}
        {url ? (
          <>
            <img
              src={url}
              alt={label}
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                bgcolor: 'rgba(0,0,0,0.45)',
                opacity: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'opacity 0.15s',
                '&:hover': { opacity: 1 },
              }}
            >
              <Tooltip title="Reemplazar foto">
                <Box
                  sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}
                >
                  <CloudUpload sx={{ color: 'white', fontSize: 32 }} />
                  <Typography variant="caption" sx={{ color: 'white', fontWeight: 700 }}>
                    Cambiar
                  </Typography>
                </Box>
              </Tooltip>
            </Box>
          </>
        ) : (
          <Box
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.5,
            }}
          >
            <CameraAlt sx={{ fontSize: 36, color: 'grey.300' }} />
            <Typography variant="caption" color="text.disabled" sx={{ fontWeight: 600 }}>
              Subir foto
            </Typography>
          </Box>
        )}
      </Box>
      {error && (
        <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
          {error}
        </Typography>
      )}
    </Box>
  );
}

// ─── Extra photo thumbnail (append-only; delete not yet supported) ─────────────

interface ExtraPhotoThumbProps {
  photo: PhotoQueueExtraPhoto;
  index: number;
}

function ExtraPhotoThumb({ photo, index }: ExtraPhotoThumbProps) {
  return (
    <Box>
      <Typography
        variant="caption"
        sx={{
          display: 'block',
          mb: 0.75,
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: 0.5,
          color: 'text.secondary',
        }}
      >
        {PHOTO_EXTRA_ANGLE_LABEL} {index + 1}
      </Typography>
      <Box
        sx={{
          height: 120,
          borderRadius: 2,
          overflow: 'hidden',
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <img
          src={photo.url}
          alt={`${PHOTO_EXTRA_ANGLE_LABEL} ${index + 1}`}
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />
      </Box>
    </Box>
  );
}

interface ExtraPhotoAddCardProps {
  isUploading: boolean;
  error: string | null;
  onSelect: () => void;
}

function ExtraPhotoAddCard({ isUploading, error, onSelect }: ExtraPhotoAddCardProps) {
  return (
    <Box>
      <Typography
        variant="caption"
        sx={{
          display: 'block',
          mb: 0.75,
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: 0.5,
          color: 'text.secondary',
        }}
      >
        Agregar {PHOTO_EXTRA_ANGLE_LABEL.toLowerCase()}
      </Typography>
      <Box
        onClick={() => {
          if (!isUploading) onSelect();
        }}
        sx={{
          height: 120,
          borderRadius: 2,
          border: '2px dashed',
          borderColor: error ? 'error.main' : 'divider',
          bgcolor: 'grey.50',
          cursor: isUploading ? 'wait' : 'pointer',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 0.5,
          transition: 'border-color 0.15s',
          '&:hover': {
            borderColor: !isUploading ? 'text.primary' : undefined,
          },
        }}
      >
        {isUploading ? (
          <CircularProgress size={28} sx={{ color: '#111827' }} />
        ) : (
          <>
            <AddAPhoto sx={{ fontSize: 32, color: 'grey.400' }} />
            <Typography variant="caption" color="text.disabled" sx={{ fontWeight: 600 }}>
              Subir foto
            </Typography>
          </>
        )}
      </Box>
      {error && (
        <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
          {error}
        </Typography>
      )}
    </Box>
  );
}

// ─── Upload modal ─────────────────────────────────────────────────────────────

interface UploadModalProps {
  item: PhotoQueueItem | null;
  onClose: (hadUpload: boolean) => void;
}

type WaiverConfirmKind = 'activate' | 'deactivate' | null;

function UploadModal({ item, onClose }: UploadModalProps) {
  const [photos, setPhotos] = useState<Partial<Record<PhotoAngle, string>>>({});
  const [extraPhotos, setExtraPhotos] = useState<PhotoQueueExtraPhoto[]>([]);
  const [uploadingAngle, setUploadingAngle] = useState<PhotoUploadAngle | null>(null);
  const [errors, setErrors] = useState<Partial<Record<PhotoAngle, string>>>({});
  const [extraError, setExtraError] = useState<string | null>(null);
  const [hadUpload, setHadUpload] = useState(false);
  const [hadWaiverChange, setHadWaiverChange] = useState(false);
  /** Optimistic override tras PATCH; null = usar valor del item del servidor. */
  const [waiverOverride, setWaiverOverride] = useState<boolean | null>(null);
  const [waiverLoading, setWaiverLoading] = useState(false);
  const [waiverError, setWaiverError] = useState<string | null>(null);
  const [waiverDialog, setWaiverDialog] = useState<WaiverConfirmKind>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pendingAngleRef = useRef<PhotoUploadAngle | null>(null);

  useEffect(() => {
    if (!item) return;
    const initial: Partial<Record<PhotoAngle, string>> = {};
    item.photos.forEach((slot) => {
      if (slot.url) initial[slot.angle] = slot.url;
    });
    setPhotos(initial);
    setExtraPhotos(item.extraPhotos);
    setErrors({});
    setExtraError(null);
    setHadUpload(false);
    setHadWaiverChange(false);
    setWaiverOverride(null);
    setWaiverError(null);
    setUploadingAngle(null);
  }, [item]);

  const handleSlotSelect = useCallback((angle: PhotoAngle) => {
    pendingAngleRef.current = angle;
    fileInputRef.current?.click();
  }, []);

  const handleExtraSelect = useCallback(() => {
    pendingAngleRef.current = 'extra';
    fileInputRef.current?.click();
  }, []);

  const handleFileChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      const angle = pendingAngleRef.current;
      e.target.value = '';
      if (!file || !angle || !item) return;

      setUploadingAngle(angle);
      if (angle === 'extra') {
        setExtraError(null);
      } else {
        setErrors((prev) => {
          const next = { ...prev };
          delete next[angle];
          return next;
        });
      }

      const result = await uploadPhotoQueueImage(item.id, angle, file);
      setUploadingAngle(null);

      if (result.ok) {
        if (angle === 'extra') {
          setExtraPhotos((prev) => [...prev, { id: -(prev.length + 1), url: result.url }]);
        } else {
          setPhotos((prev) => ({ ...prev, [angle]: result.url }));
        }
        setHadUpload(true);
      } else if (angle === 'extra') {
        setExtraError(result.error);
      } else {
        setErrors((prev) => ({ ...prev, [angle]: result.error }));
      }
    },
    [item]
  );

  const handleDialogClose = useCallback(() => {
    onClose(hadUpload || hadWaiverChange);
  }, [onClose, hadUpload, hadWaiverChange]);

  const handleCloseButtonClick = useCallback(() => {
    onClose(hadUpload || hadWaiverChange);
  }, [onClose, hadUpload, hadWaiverChange]);

  const requiredAngles = item?.isNew ? NEW_ITEM_ANGLES : USED_ITEM_ANGLES;
  const uploadedCount = requiredAngles.filter((a) => photos[a]).length;
  const waivedFromServer = item ? item.photoRequirementsWaived : false;
  const waivedDisplay = waiverOverride !== null ? waiverOverride : waivedFromServer;
  let allDone = false;
  if (item) {
    if (item.isNew) {
      allDone = uploadedCount >= requiredAngles.length;
    } else {
      allDone = waivedDisplay || uploadedCount >= requiredAngles.length;
    }
  }

  const applyWaiver = useCallback(
    async (waived: boolean) => {
      if (!item || item.isNew) return;
      setWaiverLoading(true);
      setWaiverError(null);
      const result = await patchPhotoRequirementsWaived(item.id, waived);
      setWaiverLoading(false);
      if (result.ok) {
        setWaiverOverride(result.photoRequirementsWaived);
        setHadWaiverChange(true);
        setWaiverDialog(null);
      } else {
        setWaiverError(result.error);
      }
    },
    [item]
  );

  const handleOpenActivateWaiver = useCallback(() => {
    setWaiverDialog('activate');
  }, []);

  const handleOpenDeactivateWaiver = useCallback(() => {
    setWaiverDialog('deactivate');
  }, []);

  const handleCloseWaiverDialog = useCallback(() => {
    if (!waiverLoading) setWaiverDialog(null);
  }, [waiverLoading]);

  const handleWaiverErrorDismiss = useCallback(() => {
    setWaiverError(null);
  }, []);

  const onConfirmWaiverActivate = useCallback(() => {
    applyWaiver(true);
  }, [applyWaiver]);

  const onConfirmWaiverDeactivate = useCallback(() => {
    applyWaiver(false);
  }, [applyWaiver]);

  const paperProps = useMemo(() => ({ sx: { borderRadius: 3 } as const }), []);

  return (
    <>
      <Dialog
        open={Boolean(item)}
        onClose={handleDialogClose}
        maxWidth="md"
        fullWidth
        PaperProps={paperProps}
      >
        <DialogTitle sx={{ p: 0 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              px: 3,
              pt: 3,
              pb: 2,
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Box>
              <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.2 }}>
                {item?.title}
              </Typography>
              {item && (
                <>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 0.75, display: 'block' }}
                  >
                    <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
                      UU ID:{' '}
                    </Box>
                    {item.artId != null ? `UU${item.artId}` : 'Sin asignar'}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 0.25, display: 'block' }}
                  >
                    <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
                      ID de inventario:{' '}
                    </Box>
                    {item.id}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mt: 0.75, display: 'block' }}
                  >
                    {item.isNew ? 'Producto nuevo' : 'Producto usado'}
                    {String(item.sizeCm ?? '').trim()
                      ? ` · Talla: ${tallaConUnidad(item.sizeCm)}`
                      : ' · Sin talla'}
                    {` · Consignador: ${item.consignorName}`}
                  </Typography>
                </>
              )}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 2, flexWrap: 'wrap' }}>
              <Chip
                label={
                  item && !item.isNew && waivedDisplay
                    ? 'Exención (listo p/ venta)'
                    : `${uploadedCount} / ${requiredAngles.length} fotos`
                }
                size="small"
                sx={{
                  bgcolor: allDone ? '#E8F5E9' : '#FFF8E1',
                  color: allDone ? '#2E7D32' : '#E65100',
                  fontWeight: 700,
                }}
              />
              <IconButton onClick={handleCloseButtonClick} size="small">
                <Close />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent
          sx={{
            px: 3,
            pb: 3,
            // MUI suele poner paddingTop=0 en DialogContent junto a DialogTitle
            pt: 4,
          }}
        >
          {allDone && (
            <Alert severity="success" icon={<CheckCircle />} sx={{ mb: 2, borderRadius: 2 }}>
              {item &&
              !item.isNew &&
              waivedDisplay &&
              uploadedCount < (item?.totalRequired ?? requiredAngles.length)
                ? 'Exención de fotos activa: el producto puede darse de alta para venta sin completar las 5 imágenes. Puedes cerrar esta ventana; la cola se actualizará al salir.'
                : '¡Todas las fotos han sido subidas correctamente! Puedes cerrar esta ventana.'}
            </Alert>
          )}

          {waiverError && (
            <Alert
              severity="error"
              sx={{ mb: 2, borderRadius: 2 }}
              onClose={handleWaiverErrorDismiss}
            >
              {waiverError}
            </Alert>
          )}

          <Grid container spacing={2.5} sx={{ mt: allDone ? 0 : 1.5 }}>
            {requiredAngles.map((angle) => (
              <Grid key={angle} size={{ xs: 6, sm: item?.isNew ? 12 : 4 }}>
                <PhotoSlotCard
                  angle={angle}
                  url={photos[angle] ?? null}
                  isUploading={uploadingAngle === angle}
                  error={errors[angle] ?? null}
                  onSelect={handleSlotSelect}
                />
              </Grid>
            ))}
          </Grid>

          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
              Fotos adicionales (opcional)
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
              Agrega imágenes extra si hace falta. Cada subida añade una foto nueva; no reemplaza
              las anteriores.
            </Typography>
            <Grid container spacing={2}>
              {extraPhotos.map((photo, index) => (
                <Grid key={photo.id} size={{ xs: 6, sm: 4 }}>
                  <ExtraPhotoThumb photo={photo} index={index} />
                </Grid>
              ))}
              <Grid size={{ xs: 6, sm: 4 }}>
                <ExtraPhotoAddCard
                  isUploading={uploadingAngle === 'extra'}
                  error={extraError}
                  onSelect={handleExtraSelect}
                />
              </Grid>
            </Grid>
          </Box>

          {item && !item.isNew && (
            <Box
              sx={{
                mt: 3,
                p: 2,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'grey.50',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1.5 }}>
                <Gavel sx={{ color: 'text.secondary', mt: 0.2 }} fontSize="small" />
                <Box>
                  <Typography variant="subtitle2" fontWeight={700}>
                    Producto usado: exención de las 5 fotos
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    Puedes marcar el artículo como listo para venta sin subir el set completo de
                    cinco ángulos. Al activarse, el registro deja esta cola (y puede mostrarse como
                    activo en inventario, según las reglas del sistema).
                  </Typography>
                </Box>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  gap: 1.5,
                  mt: 1,
                }}
              >
                {!waivedDisplay ? (
                  <Button
                    variant="outlined"
                    size="small"
                    disabled={waiverLoading}
                    onClick={handleOpenActivateWaiver}
                  >
                    Marcar listo para venta sin 5 fotos
                  </Button>
                ) : (
                  <Button
                    variant="outlined"
                    size="small"
                    color="warning"
                    disabled={waiverLoading}
                    onClick={handleOpenDeactivateWaiver}
                  >
                    Anular exención (volver a exigir 5 ángulos)
                  </Button>
                )}
              </Box>
              {waiverLoading && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5 }}>
                  <CircularProgress size={18} />
                  <Typography variant="caption" color="text.secondary">
                    Guardando…
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          <Box
            sx={{
              mt: 3,
              p: 2.5,
              bgcolor: '#111827',
              borderRadius: 2,
              display: 'flex',
              gap: 1.5,
            }}
          >
            <CameraAlt sx={{ color: '#EAB308', mt: 0.25, flexShrink: 0 }} />
            <Box>
              <Typography
                variant="caption"
                fontWeight={700}
                sx={{ color: 'white', display: 'block' }}
              >
                Consejo de calidad
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: 'rgba(255,255,255,0.65)', lineHeight: 1.5 }}
              >
                {item?.isNew
                  ? 'Para productos nuevos, asegúrate de que la foto sea limpia, con fondo blanco o neutro y que muestre el par completo.'
                  : 'Para productos usados, iluminación uniforme. Captura el desgaste del talón y el forro interior para evitar disputas.'}
              </Typography>
            </Box>
          </Box>
        </DialogContent>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />
      </Dialog>

      <Dialog
        open={waiverDialog === 'activate'}
        onClose={handleCloseWaiverDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Exención de requisito de fotos</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            ¿Marcar como listo para venta sin subir las cinco imágenes? El registro dejará la cola
            de fotos y, según el sistema, el inventario podrá mostrarse activo.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseWaiverDialog} disabled={waiverLoading}>
            Cancelar
          </Button>
          <Button variant="contained" onClick={onConfirmWaiverActivate} disabled={waiverLoading}>
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={waiverDialog === 'deactivate'}
        onClose={handleCloseWaiverDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Anular exención</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Volver a exigir el set de cinco ángulos. El producto podrá volver a esta cola si aún
            faltan fotos.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseWaiverDialog} disabled={waiverLoading}>
            Cancelar
          </Button>
          <Button
            color="warning"
            variant="outlined"
            onClick={onConfirmWaiverDeactivate}
            disabled={waiverLoading}
          >
            Anular exención
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

// ─── Metric summary card ──────────────────────────────────────────────────────

interface MetricCardProps {
  label: string;
  value: number | null;
  icon: React.ReactNode;
  iconBg: string;
  iconColor: string;
}

function MetricCard({ label, value, icon, iconBg, iconColor }: MetricCardProps) {
  return (
    <Card
      sx={{
        p: 2.5,
        border: '1px solid',
        borderColor: 'divider',
        boxShadow: 'none',
        borderRadius: 2,
        display: 'flex',
        alignItems: 'center',
        gap: 2,
      }}
    >
      <Box
        sx={{
          width: 48,
          height: 48,
          borderRadius: '50%',
          bgcolor: iconBg,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: iconColor,
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Box>
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
        <Typography variant="h5" fontWeight={800} sx={{ lineHeight: 1.2 }}>
          {value === null ? '—' : value}
        </Typography>
      </Box>
    </Card>
  );
}

// ─── Photo slot dot indicators ────────────────────────────────────────────────

interface PhotoSlotDotsProps {
  slots: { angle: PhotoAngle; url: string | null }[];
  waived: boolean;
}

function PhotoSlotDots({ slots, waived }: PhotoSlotDotsProps) {
  if (waived) {
    return (
      <Chip
        label="Exención activa"
        size="small"
        sx={{
          bgcolor: '#FFF7ED',
          color: '#C2410C',
          fontWeight: 700,
          fontSize: 10,
          height: 22,
          letterSpacing: 0.3,
        }}
      />
    );
  }

  const uploaded = slots.filter((s) => s.url !== null).length;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
      {slots.map((slot) => (
        <Tooltip key={slot.angle} title={PHOTO_ANGLE_LABELS[slot.angle]}>
          <Box
            sx={{
              width: 11,
              height: 11,
              borderRadius: '50%',
              bgcolor: slot.url ? '#111827' : 'transparent',
              border: '2px solid',
              borderColor: slot.url ? '#111827' : 'grey.300',
              flexShrink: 0,
              transition: 'background-color 0.15s',
            }}
          />
        </Tooltip>
      ))}
      <Typography
        variant="caption"
        sx={{ ml: 0.75, fontWeight: 700, color: 'text.secondary', whiteSpace: 'nowrap' }}
      >
        {uploaded}/{slots.length}
      </Typography>
    </Box>
  );
}

// ─── Queue row ────────────────────────────────────────────────────────────────

interface QueueRowProps {
  item: PhotoQueueItem;
  onSelect: (item: PhotoQueueItem) => void;
}

const HEADER_CELL_SX = {
  fontWeight: 700,
  fontSize: 11,
  textTransform: 'uppercase' as const,
  letterSpacing: 0.6,
  color: 'text.secondary',
  borderBottom: '2px solid',
  borderColor: 'divider',
  py: 1.5,
  bgcolor: '#FAFAFA',
};

function QueueRow({ item, onSelect }: QueueRowProps) {
  const handleRowClick = useCallback(() => {
    onSelect(item);
  }, [onSelect, item]);

  return (
    <TableRow
      hover
      onClick={handleRowClick}
      sx={{
        cursor: 'pointer',
        '&:last-child td': { border: 0 },
        '&:hover': { bgcolor: '#F8F9FA' },
      }}
    >
      {/* Type */}
      <TableCell sx={{ width: 90, pr: 1, py: 1.75 }}>
        <Chip
          label={item.isNew ? 'Nuevo' : 'Usado'}
          size="small"
          sx={{
            bgcolor: item.isNew ? '#F0FDF4' : '#FFF7ED',
            color: item.isNew ? '#15803D' : '#C2410C',
            fontWeight: 700,
            fontSize: 10,
            height: 22,
            letterSpacing: 0.3,
          }}
        />
      </TableCell>

      {/* Product + IDs */}
      <TableCell sx={{ minWidth: 240, py: 1.75 }}>
        <Typography variant="body2" fontWeight={700} sx={{ lineHeight: 1.3 }}>
          {item.title}
        </Typography>
        <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.25 }}>
          {item.artId != null ? `UU${item.artId}` : '—'} &middot; #{item.id}
        </Typography>
      </TableCell>

      {/* Size / Consignor */}
      <TableCell sx={{ width: 180, py: 1.75 }}>
        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.3 }}>
          {tallaConUnidad(item.sizeCm)}
        </Typography>
        <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.25 }}>
          {item.consignorName}
        </Typography>
      </TableCell>

      {/* Slot dots */}
      <TableCell sx={{ width: 200, py: 1.75 }}>
        <PhotoSlotDots slots={item.photos} waived={!item.isNew && item.photoRequirementsWaived} />
        {item.extraPhotos.length > 0 && (
          <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5 }}>
            +{item.extraPhotos.length} adicional
            {item.extraPhotos.length === 1 ? '' : 'es'}
          </Typography>
        )}
      </TableCell>

      {/* Action */}
      <TableCell align="right" sx={{ width: 130, py: 1.75, pr: 2 }}>
        <Button
          size="small"
          variant="outlined"
          sx={{
            borderColor: '#111827',
            color: '#111827',
            fontWeight: 700,
            fontSize: 11,
            px: 1.5,
            whiteSpace: 'nowrap',
            '&:hover': { bgcolor: '#111827', color: 'white', borderColor: '#111827' },
          }}
        >
          Subir fotos
        </Button>
      </TableCell>
    </TableRow>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

const FILTER_TABS: { value: PhotoQueueFilter; label: string }[] = [
  { value: 'all', label: 'Todos' },
  { value: 'new', label: 'Nuevos' },
  { value: 'used', label: 'Usados' },
];

function emptyStateMessage(search: string, filter: PhotoQueueFilter): string {
  if (search) return 'Intenta con otra búsqueda.';
  if (filter !== 'all') return 'Prueba con otro filtro.';
  return 'Todos los productos tienen sus fotos al día.';
}

export default function AdminPhotoQueuePage() {
  const [items, setItems] = useState<PhotoQueueItem[]>([]);
  const [summary, setSummary] = useState<PhotoQueueSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<PhotoQueueFilter>('all');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [selectedItem, setSelectedItem] = useState<PhotoQueueItem | null>(null);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const loadItems = useCallback(
    async (opts?: { search?: string; filter?: PhotoQueueFilter }) => {
      setLoading(true);
      setError(null);
      const result = await fetchPhotoQueue({
        search: opts?.search ?? search,
        filter: opts?.filter ?? filter,
      });
      if (result.ok) {
        setItems(result.data.items);
        setSummary(result.data.summary);
      } else {
        setError(result.error);
      }
      setLoading(false);
    },
    [search, filter]
  );

  useEffect(() => {
    loadItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, search]);

  const handleSearchInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setSearchInput(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setSearch(value), 400);
  }, []);

  const handleFilterChange = useCallback((_: React.SyntheticEvent, newValue: PhotoQueueFilter) => {
    setFilter(newValue);
  }, []);

  const handleCardSelect = useCallback((selected: PhotoQueueItem) => {
    setSelectedItem(selected);
  }, []);

  const handleModalClose = useCallback(
    (hadUpload: boolean) => {
      setSelectedItem(null);
      if (hadUpload) loadItems();
    },
    [loadItems]
  );

  const mainContent = (() => {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
          <CircularProgress sx={{ color: '#111827' }} />
        </Box>
      );
    }
    if (items.length === 0) {
      return (
        <Box
          sx={{
            textAlign: 'center',
            py: 12,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <CameraAlt sx={{ fontSize: 64, color: 'grey.300' }} />
          <Typography variant="h6" color="text.secondary">
            No hay ítems pendientes de fotografía
          </Typography>
          <Typography variant="body2" color="text.disabled">
            {emptyStateMessage(search, filter)}
          </Typography>
        </Box>
      );
    }
    return (
      <TableContainer
        component={Paper}
        sx={{ boxShadow: 'none', border: '1px solid', borderColor: 'divider', borderRadius: 2 }}
      >
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={HEADER_CELL_SX}>Tipo</TableCell>
              <TableCell sx={HEADER_CELL_SX}>Producto</TableCell>
              <TableCell sx={HEADER_CELL_SX}>Talla / Consignador</TableCell>
              <TableCell sx={HEADER_CELL_SX}>Fotos pendientes</TableCell>
              <TableCell sx={HEADER_CELL_SX} />
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((queueItem) => (
              <QueueRow key={queueItem.id} item={queueItem} onSelect={handleCardSelect} />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  })();

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#F9FAFB', minHeight: '100vh' }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { md: 'flex-end' },
          justifyContent: 'space-between',
          gap: 2,
          mb: 4,
        }}
      >
        <Box>
          <Typography variant="h4" fontWeight={800} sx={{ mb: 0.5, textAlign: 'left' }}>
            Cola de Fotos
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Gestiona las fotografías pendientes del inventario nuevo y usado.
          </Typography>
        </Box>

        <TextField
          size="small"
          placeholder="Buscar modelo, talla, consignador..."
          value={searchInput}
          onChange={handleSearchInputChange}
          sx={{ width: { xs: '100%', md: 280 }, bgcolor: 'white' }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <CameraAlt fontSize="small" sx={{ color: 'text.disabled' }} />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Summary cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, sm: 4 }}>
          <MetricCard
            label="Ítems pendientes"
            value={summary?.totalPending ?? null}
            icon={<PendingActions />}
            iconBg="#EFF6FF"
            iconColor="#3B82F6"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <MetricCard
            label="Nuevos (1 foto)"
            value={summary?.newPending ?? null}
            icon={<NewReleases />}
            iconBg="#F0FDF4"
            iconColor="#22C55E"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <MetricCard
            label="Usados (5 fotos)"
            value={summary?.usedPending ?? null}
            icon={<AddAPhoto />}
            iconBg="#FFF7ED"
            iconColor="#F97316"
          />
        </Grid>
      </Grid>

      {/* Filter tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs
          value={filter}
          onChange={handleFilterChange}
          sx={{
            '& .MuiTab-root': { textTransform: 'none', fontWeight: 600 },
            '& .Mui-selected': { color: '#111827 !important' },
            '& .MuiTabs-indicator': { bgcolor: '#111827' },
          }}
        >
          {FILTER_TABS.map((t) => (
            <Tab key={t.value} value={t.value} label={t.label} />
          ))}
        </Tabs>
      </Box>

      {/* Content */}
      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {mainContent}

      {/* Upload modal */}
      <UploadModal item={selectedItem} onClose={handleModalClose} />
    </Box>
  );
}
